import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { loginRequest } from "../auth/msalConfig";
import { theme } from "../theme";
import { SIGN_IN as C } from "../lib/signInPalette";
import { BlueprintBackdrop } from "../components/BlueprintBackdrop";
import { startLocalSession } from "../api/client";

type Role = "client" | "consultant" | "auditor";

const API = import.meta.env.VITE_API_BASE_URL as string;
const MAIN_APP_URL = (import.meta.env.VITE_MAIN_APP_URL ?? "https://app.abacassecurity.com").replace(/\/+$/, "");

// Password sign-in is two steps: password, then the second factor — the
// authenticator app once enrolled, an emailed code until then (after which the
// app forces enrolment before anything else).
type PasswordStep =
  | { kind: "credentials" }
  | { kind: "code"; challengeId: string; method: "totp" | "email"; useRecovery: boolean };

const ROLES: Array<{ id: Role; label: string; description: string }> = [
  {
    id: "client",
    label: "Client",
    description: "Your organisation is working towards one or more compliance certifications.",
  },
  {
    id: "consultant",
    label: "Consultant",
    description: "You support client organisations on their compliance journey.",
  },
  {
    id: "auditor",
    label: "Auditor",
    description: "You are a certification auditor assessing client evidence.",
  },
];

// The shared dark sign-in palette (lib/signInPalette.ts).

// ── Login page ────────────────────────────────────────────────────────────

export function LoginPage({ onLocalLogin, heading = "Abacas Security", tagline = "Always Believe Anyone Can Achieve Success", clientOnly = false }: {
  onLocalLogin?: () => void;
  /** The line under the logo; the Jersey edition (frontend-jsy) supplies its own. */
  heading?: string;
  tagline?: string;
  /** No role picker: client sign-in only. The Jersey edition's consultants and
   *  auditors work from the main platform, which JsyApp sends them to anyway. */
  clientOnly?: boolean;
}) {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<Role | null>(clientOnly ? "client" : null);
  const [error,         setError]         = useState("");
  const [loading,       setLoading]       = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);

  // Email + password flow (null = the SSO buttons are showing).
  const [pw, setPw]                 = useState<PasswordStep | null>(null);
  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [code, setCode]             = useState("");
  const [pwBusy, setPwBusy]         = useState(false);

  // Back from Google SSO or SAML: the API has already put the session in its HttpOnly
  // cookie, and the fragment only says so. The first request fetches the access token.
  useEffect(() => {
    const hashParams   = new URLSearchParams(window.location.hash.slice(1));
    const searchParams = new URLSearchParams(window.location.search);
    const gaSession = hashParams.get("ga_session") === "1";
    const googleErr = searchParams.get("google_error");

    if (gaSession) {
      startLocalSession(null, true);
      window.history.replaceState({}, "", window.location.pathname);
      onLocalLogin?.();
      return;
    }

    if (googleErr) {
      window.history.replaceState({}, "", window.location.pathname);
      const MESSAGES: Record<string, string> = {
        cancelled:      "Google sign-in was cancelled.",
        invalid_state:  "The sign-in link expired. Please try again.",
        auth_failed:    "Google authorisation failed. Please try again.",
        token_invalid:  "Could not verify your Google account. Please try again.",
        not_invited:    "Your Google account hasn't been invited to this platform yet.",
        invite_invalid: "The invite link is no longer valid. Please request a new one.",
        email_mismatch: "Your Google account email doesn't match the invite.",
        // The Google invite callback lands here on a bureau invite whose address
        // has become an active account elsewhere (lib/inviteRedemption.ts).
        account_conflict: "That email address is already an active account elsewhere (a client, consultant or another bureau's auditor), so it cannot be moved into this bureau. Ask the bureau to invite a different address.",
        // The account's sign-in policy (backend/src/lib/signInPolicy.ts).
        sso_required:         "Your organisation requires you to sign in with Microsoft or Google Workspace.",
        personal_account:     "Sign in with your work account. Personal Microsoft and Google accounts are not accepted.",
        tenant_not_allowed:   "That work account belongs to a Microsoft tenant or Google Workspace your organisation has not approved. Ask your administrator.",
        provider_not_allowed: "Your organisation does not allow signing in with Google.",
      };
      setError(MESSAGES[googleErr] ?? "Google sign-in failed. Please try again.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSignIn() {
    if (!selectedRole) return;
    setError("");
    setLoading(true);
    try {
      sessionStorage.setItem("abacas.intendedRole", selectedRole);
      await instance.loginRedirect(loginRequest);
    } catch {
      setError("Could not start Microsoft sign-in. Please try again.");
      setLoading(false);
    }
  }

  async function postJson(path: string, body: unknown) {
    // The base session slot and credentials: a successful sign-in sets the session's
    // HttpOnly cookie on the API (backend/src/lib/sessionCookies.ts).
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Abacas-Session": "base" },
      body: JSON.stringify(body),
      credentials: "include",
    });
    const data = await res.json().catch(() => ({})) as Record<string, unknown>;
    return { res, data };
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pw || pw.kind !== "credentials") return;
    setError(""); setPwBusy(true);
    try {
      if (selectedRole) sessionStorage.setItem("abacas.intendedRole", selectedRole);
      const { res, data } = await postJson("/auth/login", { email: email.trim(), password });
      if (res.status === 429) { setError("Too many attempts. Please wait a few minutes and try again."); return; }
      if (!res.ok) {
        // A policy refusal carries a sentence (`message`); a failed password carries only `error`.
        setError(typeof data.message === "string" ? data.message : typeof data.error === "string" ? data.error : "Invalid email or password");
        return;
      }
      const method = data.status === "totp_required" ? "totp" : "email";
      setPassword("");
      setCode("");
      setPw({ kind: "code", challengeId: String(data.challengeId), method, useRecovery: false });
    } catch {
      setError("Could not reach the sign-in service. Please try again.");
    } finally {
      setPwBusy(false);
    }
  }

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pw || pw.kind !== "code") return;
    setError(""); setPwBusy(true);
    try {
      const body = pw.useRecovery
        ? { challengeId: pw.challengeId, recoveryCode: code.trim() }
        : { challengeId: pw.challengeId, code: code.replace(/\s/g, "") };
      const { res, data } = await postJson("/auth/verify-otp", body);
      if (res.status === 429) { setError("Too many attempts. Please wait a few minutes and try again."); return; }
      if (!res.ok) { setError("That code was not accepted. Check it and try again, or sign in again for a fresh code."); return; }
      // The enrolment-only session cannot be refreshed; the app then shows the
      // authenticator setup and nothing else.
      startLocalSession(String(data.accessToken), data.refreshable === true);
      onLocalLogin?.();
    } catch {
      setError("Could not reach the sign-in service. Please try again.");
    } finally {
      setPwBusy(false);
    }
  }

  function backToChoices() {
    setPw(null); setError(""); setPassword(""); setCode("");
  }

  async function handleGoogleSignIn() {
    setError("");
    setLoadingGoogle(true);
    try {
      if (selectedRole) sessionStorage.setItem("abacas.intendedRole", selectedRole);
      const API = import.meta.env.VITE_API_BASE_URL;
      // credentials: the API sets a nonce cookie that the SSO callback checks.
      const res = await fetch(`${API}/auth/google/sso-url`, { credentials: "include" });
      const body = await res.json() as { url?: string; error?: string };
      if (!res.ok || !body.url) {
        setError(body.error ?? "Could not start Google sign-in. Please try again.");
        setLoadingGoogle(false);
        return;
      }
      window.location.href = body.url;
    } catch {
      setError("Could not start Google sign-in. Please try again.");
      setLoadingGoogle(false);
    }
  }

  const fieldLabel: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: C.text };
  const fieldInput: React.CSSProperties = {
    width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: theme.radius.sm,
    border: `1px solid ${C.border}`, background: "rgba(255,255,255,0.06)", color: C.heading, fontSize: 13, outline: "none",
  };
  const primaryButton = (disabled: boolean): React.CSSProperties => ({
    width: "100%", padding: "10px 18px", borderRadius: theme.radius.sm, border: "none", background: C.accent,
    color: C.btnText, fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1,
  });
  const linkButton: React.CSSProperties = { background: "none", border: "none", color: C.subtle, fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 };

  const btnBase: React.CSSProperties = {
    display: "block",
    width: "100%",
    padding: "14px 16px",
    borderRadius: theme.radius.md,
    cursor: "pointer",
    textAlign: "left",
    marginBottom: 8,
    background: "transparent",
    transition: "border-color 0.1s, background 0.1s",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.font,
        padding: 16,
        position: "relative",
      }}
    >
      <BlueprintBackdrop surface="login" />

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative", zIndex: 1, width: "100%", maxWidth: 440 }}>
        {/* Branding above the card */}
        <img
          src="/abacas-logo.png"
          alt="Abacas Security"
          style={{ width: 120, height: 120, objectFit: "contain", marginBottom: 20, filter: "drop-shadow(0 0 24px rgba(255,0,170,0.6))" }}
        />
        <div style={{ fontSize: 38, fontWeight: 800, color: "#ffffff", letterSpacing: "-0.5px", textAlign: "center", lineHeight: 1.1, marginBottom: 10, textShadow: "0 0 32px rgba(255,0,170,0.5)" }}>
          {heading}
        </div>
        <div style={{ fontSize: 15, fontWeight: 500, color: "#e0b4f5", textAlign: "center", marginBottom: 32, letterSpacing: "0.2px", textShadow: "0 0 16px rgba(200,0,255,0.3)" }}>
          {tagline}
        </div>

      <div
        style={{
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: theme.radius.lg,
          padding: 24,
          width: "100%",
          position: "relative",
          boxShadow: "var(--c-sheetShadow)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, color: C.heading, marginBottom: 4 }}>
          Compliance Platform
        </div>
        <div style={{ fontSize: 13, color: C.subtle, marginBottom: clientOnly ? 8 : 24 }}>
          {clientOnly ? "Sign in to your organisation's compliance portal." : "How are you signing in today?"}
        </div>

          <>
            {!clientOnly && ROLES.map((role) => {
              const isSelected = selectedRole === role.id;
              return (
                <button
                  key={role.id}
                  onClick={() => setSelectedRole(role.id)}
                  style={{
                    ...btnBase,
                    border: `1px solid ${isSelected ? C.borderSel : C.border}`,
                    background: isSelected ? C.accentBg : "transparent",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div
                      style={{
                        width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                        border: `2px solid ${isSelected ? C.accent : "#d1d5db"}`,
                        background: isSelected ? C.accent : "transparent",
                      }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.heading, marginBottom: 2 }}>
                        {role.label}
                      </div>
                      <div style={{ fontSize: 12, color: C.subtle }}>{role.description}</div>
                    </div>
                  </div>
                </button>
              );
            })}

            {selectedRole && pw?.kind === "credentials" && (
              <form onSubmit={handlePasswordSubmit} style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={fieldLabel} htmlFor="login-email">Email address</label>
                <input id="login-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} style={fieldInput} autoFocus />
                <label style={fieldLabel} htmlFor="login-password">Password</label>
                <input id="login-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} style={fieldInput} />
                <button type="submit" disabled={pwBusy || !email.trim() || !password} style={primaryButton(pwBusy || !email.trim() || !password)}>
                  {pwBusy ? "Checking…" : "Continue"}
                </button>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <button type="button" onClick={backToChoices} style={linkButton}>Other sign-in options</button>
                  <button type="button" onClick={() => navigate("/forgot-password")} style={linkButton}>Forgot password?</button>
                </div>
              </form>
            )}

            {selectedRole && pw?.kind === "code" && (
              <form onSubmit={handleCodeSubmit} style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontSize: 12, color: C.subtle, lineHeight: 1.6 }}>
                  {pw.useRecovery
                    ? "Enter one of the recovery codes you saved when you set up your authenticator app. Each code works once."
                    : pw.method === "totp"
                      ? "Enter the six-digit code from your authenticator app."
                      : <>We have emailed a six-digit code to <strong>{email.trim()}</strong>. It expires in 10 minutes. You will set up your authenticator app next.</>}
                </div>
                <input
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(pw.useRecovery ? e.target.value.slice(0, 16) : e.target.value.replace(/[^\d\s]/g, "").slice(0, 7))}
                  inputMode={pw.useRecovery ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  placeholder={pw.useRecovery ? "xxxxx-xxxxx" : "123 456"}
                  aria-label={pw.useRecovery ? "Recovery code" : "Verification code"}
                  style={{ ...fieldInput, fontSize: 18, letterSpacing: "0.12em", textAlign: "center" }}
                />
                <button type="submit" disabled={pwBusy || !code.trim()} style={primaryButton(pwBusy || !code.trim())}>
                  {pwBusy ? "Checking…" : "Sign in"}
                </button>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <button type="button" onClick={backToChoices} style={linkButton}>Start again</button>
                  {pw.method === "totp" && (
                    <button type="button" onClick={() => { setPw({ ...pw, useRecovery: !pw.useRecovery }); setCode(""); setError(""); }} style={linkButton}>
                      {pw.useRecovery ? "Use my authenticator app" : "Use a recovery code"}
                    </button>
                  )}
                </div>
              </form>
            )}

            {selectedRole && !pw && (
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                <button
                  onClick={handleSignIn}
                  disabled={loading || loadingGoogle}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    width: "100%", padding: "10px 18px",
                    borderRadius: theme.radius.sm, border: "none", background: C.accent,
                    color: C.btnText, fontSize: 13, fontWeight: 600, cursor: "pointer",
                    opacity: loading || loadingGoogle ? 0.7 : 1,
                  }}
                >
                  {loading ? "Redirecting…" : "Sign in with Microsoft"}
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, height: 1, background: C.border }} />
                  <span style={{ fontSize: 11, color: C.subtle }}>or</span>
                  <div style={{ flex: 1, height: 1, background: C.border }} />
                </div>
                <button
                  onClick={handleGoogleSignIn}
                  disabled={loading || loadingGoogle}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    width: "100%", padding: "10px 18px",
                    borderRadius: theme.radius.sm, border: `1px solid ${C.border}`,
                    background: "rgba(255,255,255,0.07)", color: C.heading,
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                    opacity: loading || loadingGoogle ? 0.7 : 1,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  {loadingGoogle ? "Redirecting…" : "Sign in with Google"}
                </button>
                <button
                  onClick={() => { setPw({ kind: "credentials" }); setError(""); }}
                  disabled={loading || loadingGoogle}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    width: "100%", padding: "10px 18px",
                    borderRadius: theme.radius.sm, border: `1px solid ${C.border}`,
                    background: "rgba(255,255,255,0.07)", color: C.heading,
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                    opacity: loading || loadingGoogle ? 0.7 : 1,
                  }}
                >
                  Sign in with email and password
                </button>
              </div>
            )}

            {error && <div style={{ fontSize: 12, color: C.danger, marginTop: 12 }}>{error}</div>}
          </>
      </div>

        {clientOnly && (
          <div style={{ marginTop: 16, fontSize: 12, color: C.subtle, textAlign: "center", lineHeight: 1.6 }}>
            Consultants and auditors sign in at{" "}
            <a href={MAIN_APP_URL} style={{ color: C.text, fontWeight: 600 }}>{new URL(MAIN_APP_URL).host}</a>
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 20, textAlign: "center" }}>
          <button
            onClick={() => navigate("/privacy-policy")}
            style={{
              background: "none",
              border: "none",
              color: C.subtle,
              fontSize: 12,
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            Privacy Policy
          </button>
        </div>
      </div>
    </div>
  );
}
